<template>
  <div>
    <div :class="`${widgetIndex}-chart-cancelation`" :ref="`${widgetIndex}-chart-cancelation`" style="width: 100%; height: 340px;">
      <p class="loading__data" style="margin: 8px; text-align: center;">Loading...</p>
    </div>
    <div ref="detail" class="status--detail" style="width: 100%;">
      <ul>
        <li @click="toListMode(dt)" title="Click to see detail" v-for="(dt, index) in statusData"
          :key="index">
          <span :class="dt.className">{{ dt.status }}</span>
          <span :class="dt.className" class="pull-right">{{ dt.count }}</span>
        </li>
      </ul>
    </div>
    <transition name="slide-fade">
      <div ref="detail--list" v-show="listMode" class="detail--list">
        <div class="detail__list-header" :class="`${currentList.className}-header-list`">
          <p style="margin-bottom: 0px; font-weight: bold; color: white;" class="text-center">{{ currentList.status }} ({{ currentList.data ? currentList.data.length : 0  }})</p>
          <i title="Close" @click="listMode = false" class="fa fa-times-circle detail__list-close"></i>
        </div>
        <div ref="detail-list--container" class="detail-list--container">
          <ul>
            <li v-for="(dt, index) in currentList.data"
              :key="index">
              <b>{{ index + 1 }}</b> -
              <span v-if="currentList.className === 'shipment_deliveries__status'">
                <router-link :to="`/shipment_md/shipments/${dt.id}`">{{ dt.number }}</router-link> &#183; {{ get(dt, 'driver.name') }} (<b>{{ get(dt, 'state', '').replace('_', ' ') }}</b>)
                &#183; {{ formatDate(get(dt, 'metadata.shipment_date_string')) }}
              </span>
              <span v-else>
                <router-link :to="`/shipment_md/orders/${dt.id}`">{{ dt.number }}</router-link> &#183; {{ get(dt, 'shipment.driver.name') }} &#183; <span class="text-danger">{{ getOrderCancelReason(dt) }}</span>
                &#183; {{ formatDate(get(dt, 'shipment.metadata.shipment_date_string')) }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import 'amcharts3';
import 'amcharts3/amcharts/pie';
import 'amcharts3/amcharts/plugins/animate/animate';
import 'amcharts3/amcharts/themes/light';
import dateMixin from '@/mixins/date';
import { get } from 'lodash';
import moment from 'moment';

export default {
  name: 'orders-cancelation',
  mixins: [dateMixin],
  data() {
    return {
      timeout: null,
      statusData: [],
      currentList: [],
      listMode: false,
      cached_data: {},
    };
  },
  props: {
    widgetIndex: {
      type: String,
      required: true,
    },
    range: {
      type: String,
      required: true,
    },
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
  },
  mounted() {
    this.createChart();
  },
  watch: {
    range(newRange) {
      if (this.cached_data.range !== newRange) {
        this.createChart();
        this.cached_data.range = newRange;
      }
    },
    startDate(date) {
      if (this.cached_data.start_date !== date) {
        this.createChart();
        this.cached_data.start_date = date;
      }
    },
    endDate(date) {
      if (this.cached_data.end_date !== date) {
        this.createChart();
        this.cached_data.end_date = date;
      }
    },
  },
  methods: {
    get(obj, key, defaultVal = '') {
      return get(obj, key, defaultVal);
    },
    getOrderCancelReason(order) {
      let reason = '';

      if (order.metadata.abort) {
        reason = get(order, 'metadata.abort.reason', '');
      } else if (order.metadata.rejected) {
        reason = get(order, 'metadata.rejected.reason', '');
      }

      return reason;
    },
    createChart: async function createChart() {
      setTimeout(() => {
        const idxRef = `${this.widgetIndex}-chart-cancelation`;
        const chartElem = this.$refs[idxRef];

        this.setUpHeight();

        const startHumanize = this.formatDate(this.startDate);
        const endHumanize = this.formatDate(this.endDate);
        const subTitle = moment(this.startDate).isSame(this.endDate) ? startHumanize : `${startHumanize} - ${endHumanize}`;

        const chartConfig = {
          titles: [
            { text: this.getChartTitle(), size: 17 },
            { text: subTitle, bold: false },
          ],
          type: 'pie',
          fontFamily: "'Open Sans', sans-serif",
          theme: 'light',
          dataProvider: [],
          valueField: 'count',
          titleField: 'status',
          colorField: 'color',
          startDuration: 0,
          innerRadius: 30,
          pullOutRadius: 20,
          marginTop: 30,
          legend: {
            position: 'bottom',
            marginRight: 100,
            autoMargins: false,
          },
          listeners: [{
            event: 'init',
            method: (e) => {
              const chartObj = e.chart;
              const loop = async () => {
                const data = await this.loadData();
                const shipmentChartData = this.prepareShipmentData(data);
                this.statusData = this.prepareStatusData(data);

                chartObj.animateData(shipmentChartData, {
                  duration: 1e3,
                  complete: () => {
                    this.reloadChartData(loop);
                  },
                });
              };

              loop();
              this.removeChartTitleLink();
            },
          }],
        };

        window.AmCharts.makeChart(chartElem, chartConfig);
      }, 5e2);
    },
    loadData() {
      return this.$store.dispatch('shipment_md/shipments/GET_CANCELATION_ORDERS', {
        start_date: this.startDate,
        end_date: this.endDate,
      });
    },
    reloadChartData(load) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        load();
      }, 5e3);
    },
    prepareShipmentData(data) {
      const prettified = [];

      const { shipment_deliveries = [] } = data;
      const groupingData = shipment_deliveries.reduce((newFormat, dt) => {
        const { state } = dt;
        if (!newFormat[state]) {
          newFormat[state] = {
            count: 0,
            data: [],
          };
        }

        newFormat[state].count++;
        newFormat[state].data.push(dt);
        return newFormat;
      }, {});

      Object.keys(groupingData).map((key) => {
        const statusText = key.replace(new RegExp('_', 'g'), ' ').toUpperCase();

        prettified.push({
          status: statusText,
          count: groupingData[key].count,
        });
        return key;
      });

      return prettified;
    },
    prepareStatusData(data) {
      const prettified = [];

      Object.keys(data).map((key) => {
        const statusText = key.replace(new RegExp('_', 'g'), ' ').toUpperCase();

        prettified.push({
          className: `${key}__status`,
          status: statusText,
          data: data[key],
          count: data[key].length,
        });
        return key;
      });

      return prettified;
    },
    getChartTitle() {
      let title = this.range === 'custom' ? 'custom_date_range' : this.range;

      title = title
        .replace(new RegExp('_', 'g'), ' ')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

      return title;
    },
    setUpHeight() {
      const idxRef = `${this.widgetIndex}-chart-cancelation`;

      if (this.$refs[idxRef]) {
        const wItem = this.$parent.$refs.item || { clientHeight: 0 };
        const newHeight = wItem.clientHeight - 128;

        this.$refs[idxRef].style.height = `${newHeight}px`;
        this.$refs.detail.style.height = `${wItem.clientHeight - 380}px`;
        this.$refs['detail--list'].style.height = `${wItem.clientHeight - 43}px`;
        this.$refs['detail-list--container'].style.height = `${wItem.clientHeight - 73}px`;
      }
    },
    removeChartTitleLink() {
      const amChartTitle = document.querySelectorAll('a[title="JavaScript charts"]');
      for (let i = 0; i < amChartTitle.length; i++) {
        const chartTitle = amChartTitle[i];
        chartTitle.remove();
      }
    },
    toListMode(dt) {
      this.listMode = true;
      this.currentList = dt;
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
};
</script>
<style lang="sass" scoped>
  .slide-fade-enter-active
    transition: all .6s ease;

  .slide-fade-leave-active
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);

  .slide-fade-enter, .slide-fade-leave-to
    transform: translateY(-20px);
    opacity: 0;

  .status--detail > ul
    margin-bottom: 0px;
    list-style: none;
    padding-left: 0px;

  .status--detail > ul > li
    padding: 10px 15px;
    border-top: 1px solid #f4f4f4;
    cursor: pointer;

  .status--detail > ul > li:hover
    background: #f7f7f7;

  .shipment_deliveries__status
    color: #00A65A;
    font-weight: bold;

  .shipment_deliveries__status-header-list
    background: #00A65A;

  .cancelation_orders__status
    color: #F56954;
    font-weight: bold;

  .cancelation_orders__status-header-list
    background: #F56954;

  .detail--list
    width: 100%;
    position: absolute;
    top: 40px;
    background: white;

  .detail-list--container
    overflow-y: auto;

  .detail__list-close
    position: absolute;
    right: 5px;
    top: 8px;
    cursor: pointer;
    color: #fff;

  .detail__list-close:hover
    color: #d6d6d6;

  .detail__list-header
    height: 30px;
    padding: 4px;

  .detail-list--container > ul
    list-style: none;
    padding: 0px 8px 0px 8px;

  .detail-list--container > ul > li
    padding-top: 8px;
    border-bottom: 1px solid #7b7b7b;
</style>



// WEBPACK FOOTER //
// src/components/dashboard_widgets/OrdersCancelation.vue